<script setup>
import axios from "axios";
import { onBeforeMount, onMounted, onUnmounted, ref } from "vue";
import { Controls } from "@vue-flow/controls";
import { useVueFlow } from "@vue-flow/core";
import DialogSettings from "./DialogSettings.vue";
import { validate } from "../mixins/VueFlowEditor.js";
import SidebarAssistant from "./SidebarAssistant.vue";

const props = defineProps(["api", "apiDraw", "apiChat", "tenantId"]);
//String key used to save mashup on sessionStorage
const flowKey = "mashup";
//Flow editor settings
let flowSettings = ref({ showNodeSettings: false });
//Manage dialog settings activator
let showDialogSettings = ref(false);
//Manage Assistant drawer
let showDrawerAssistant = ref(false);
//If loaded mashup is released or not
let mashupReleased = ref(false);
//Chat status for AI Assistant
let chatEnabled = ref(false);

const emit = defineEmits(["setSnackbar", "preview", "validate"]);
//Refresh mashup with API
const refreshMashup = () => {
    getMashup();
};

onBeforeMount(async () => {
    // Check chat status before mounting
    chatEnabled.value = await getChatStatus();
});

onMounted(() => {
    // Add an event listener for the custom event "refresh-mashup-version"
    // When the event is triggered from version select, execute the getMashup() function
    document.addEventListener("refresh-mashup-version", refreshMashup);
    // Event that listens to start the release process
    document.addEventListener("validate-mashup", onRelease);
    // Loads the mashup from the API
    getMashup();
});

onUnmounted(() => {
    // Remove event listener when component is unmounted
    document.removeEventListener("refresh-mashup-version", refreshMashup);
    // Remove event listener when component is unmounted
    document.removeEventListener("validate-mashup", onRelease);
});

const {
    fitView,
    zoomIn,
    zoomOut,
    nodesDraggable,
    setNodes,
    setEdges,
    setViewport,
    toObject,
} = useVueFlow();

//Call mashup Release
async function onRelease() {
    let debug = await validate(props.apiDraw, { draw: toObject() });
    if (debug.errorCounter === 0) {
        await onSave().then(() => {
            document.dispatchEvent(
                new CustomEvent("release-valid-mashup", {
                    detail: { result: true },
                }),
            );
        });
    } else {
        emit("validate", debug);
        emit("setSnackbar", true, "strings.mashup-not-valid", "red");
        document.dispatchEvent(
            new CustomEvent("release-valid-mashup", {
                detail: { result: false },
            }),
        );
    }
}

//Call mashup Validation
async function onValidate() {
    let debug = await validate(props.apiDraw, { draw: toObject() });
    emit("validate", debug);
}

//Call mashup Preview
function onPreview() {
    emit("preview", true, "Output", "output");
}

//Set flow editor user settings
function onSettings(value, settings) {
    showDialogSettings.value = value;
    flowSettings.value = settings;
    localStorage.setItem("flowSettings", JSON.stringify(flowSettings.value));
}

//Toggle Assistant drawer
function toggleDrawerAssistant(value) {
    showDrawerAssistant.value = value;
}

//Toggle nodes draggable
function onToggleInteractive() {
    nodesDraggable.value = !nodesDraggable.value;
}

//Restore mashup from sessionStorage
function onRestore() {
    const mashup = JSON.parse(sessionStorage.getItem(flowKey));
    if (mashup) {
        const [x = 0, y = 0] = mashup.position;
        setNodes(mashup.nodes);
        setEdges(mashup.edges);
        setViewport({ x, y, zoom: mashup.zoom || 0 });
    }
}

//Save mashup with API: /v3/dev/draw/{project-id}/v/{version-id}/save
async function onSave() {
    sessionStorage.setItem(flowKey, JSON.stringify(toObject()));
    const savePath =
        props.apiDraw + window.location.pathname.replace("/edit", "/save");
    const data = JSON.stringify({ draw: toObject() });

    try {
        const response = await axios.post(savePath, data, {
            headers: {
                "Content-Type": "application/json",
            },
            withCredentials: true,
        });
        emit("setSnackbar", true, "strings.save-complete", "green");
        emit("validate", response.data.debug);
        return response.data;
    } catch (error) {
        emit("setSnackbar", true, "strings.something-went-wrong", "red");
        console.error(`An error has occurred: ${error}`);
    }
}

// Retrieve mashup with API: /v3/dev/draw/{project-id}/v/{version-id}/edit
async function getMashup() {
    toggleOverlay(true);
    const drawPath = props.apiDraw + window.location.pathname;

    try {
        const response = await axios.get(drawPath, {
            withCredentials: true,
        });
        const mashup = response.data;

        if (mashup.edges.length > 0 || mashup.nodes.length > 0) {
            sessionStorage.setItem(flowKey, JSON.stringify(mashup));
        }
        //Get the release status for the mashup by checking the header agreed with the API
        mashupReleased.value =
            Boolean(response.headers["x-dme-released"]) || false;
        onRestore();
        toggleOverlay(false);
        onValidate();
    } catch (error) {
        console.error(`An error has occurred: ${error}`);
        toggleOverlay(false);
    }
}

function toggleOverlay(released) {
    document.dispatchEvent(
        new CustomEvent("toggle-overlay", {
            detail: {
                released: released,
            },
        }),
    );
}

async function getChatStatus() {
    if (!props.apiChat) return false;
    const chatStatus =
        props.apiChat.replace("chat", "api") +
        `/${props.tenantId}/status/chat-enabled`;
    try {
        const response = await axios.get(chatStatus, {
            withCredentials: true,
        });
        return response.data.chat.enabled;
    } catch (error) {
        console.error(`An error has occurred: ${error}`);
        return false;
    }
}
</script>

<template>
    <SidebarAssistant
        :apiChat="props.apiChat"
        :tenant="props.tenantId"
        :showDrawerAssistant="showDrawerAssistant"
        @toggleDrawerAssistant="toggleDrawerAssistant"
        @refreshMashup="getMashup"
    />
    <DialogSettings
        :showDialogSettings="showDialogSettings"
        @toggleModalSettings="onSettings"
    />
    <v-alert
        v-if="mashupReleased"
        class="controlBanner d-flex align-center justify-center position-absolute top-0 w-100"
        border-top
        type="warning"
        density="compact"
        variant="tonal"
    >
        {{ $t("strings.released-mashup-alert") }}
    </v-alert>

    <div
        class="controlPanel d-flex justify-center w-100 position-absolute bottom-0 flex-nowrap align-content-center align-end"
    >
        <Controls position="bottom-left bottom-right" :showInteractive="false">
            <template #control-zoom-in>
                <v-tooltip :text="$t('labels.zoom-in')" location="top">
                    <template v-slot:activator="{ props }">
                        <v-btn
                            class=""
                            flat
                            icon="mdi-magnify-plus-outline"
                            size="default"
                            @click="zoomIn()"
                            v-bind="props"
                            variant="text"
                            color="primary"
                        ></v-btn>
                    </template>
                </v-tooltip>
            </template>
            <template #control-zoom-out>
                <v-tooltip :text="$t('labels.zoom-out')" location="top">
                    <template v-slot:activator="{ props }">
                        <v-btn
                            class=""
                            flat
                            icon="mdi-magnify-minus-outline"
                            size="default"
                            @click="zoomOut()"
                            v-bind="props"
                            variant="text"
                            color="primary"
                        ></v-btn>
                    </template>
                </v-tooltip>
            </template>
            <template #control-fit-view>
                <v-tooltip :text="$t('labels.fit-mashup')" location="top">
                    <template v-slot:activator="{ props }">
                        <v-btn
                            class=""
                            flat
                            icon="mdi-target-variant"
                            size="default"
                            @click="
                                fitView({
                                    padding: 0.25,
                                    includeHiddenNodes: true,
                                })
                            "
                            v-bind="props"
                            variant="text"
                            color="primary"
                        ></v-btn>
                    </template>
                </v-tooltip>
            </template>
            <v-divider class="mx-1" vertical></v-divider>
            <v-tooltip :text="$t('strings.validate-mashup')" location="top">
                <template v-slot:activator="{ props }">
                    <v-btn
                        class="mx-1"
                        color="secondary"
                        variant="text"
                        flat
                        icon="mdi-checkbox-marked-circle-outline"
                        @click="onValidate()"
                        v-bind="props"
                    >
                    </v-btn>
                </template>
            </v-tooltip>
            <v-tooltip :text="$t('strings.run-preview-mashup')" location="top">
                <template v-slot:activator="{ props }">
                    <v-btn
                        class="mx-1 text-white"
                        color="secondary"
                        flat
                        text="Run"
                        append-icon="mdi-play-box"
                        @click="onPreview()"
                        v-bind="props"
                    >
                    </v-btn>
                </template>
            </v-tooltip>
            <v-divider class="mx-1" vertical></v-divider>
            <v-tooltip :text="$t('labels.settings')" location="top">
                <template v-slot:activator="{ props }">
                    <v-btn
                        flat
                        icon="mdi mdi-tune-vertical-variant"
                        size="default"
                        @click="showDialogSettings = !showDialogSettings"
                        v-bind="props"
                        variant="text"
                        color="primary"
                    ></v-btn>
                </template>
            </v-tooltip>
            <v-divider class="mx-1" vertical></v-divider>
            <v-tooltip
                v-if="chatEnabled"
                :text="$t('strings.ai-assistant')"
                location="top"
            >
                <template v-slot:activator="{ props }">
                    <v-btn
                        flat
                        icon="mdi mdi-creation"
                        size="default"
                        @click="showDrawerAssistant = !showDrawerAssistant"
                        v-bind="props"
                        variant="text"
                        color="primary"
                    ></v-btn>
                </template>
            </v-tooltip>
            <v-divider v-if="chatEnabled" class="mx-1" vertical></v-divider>
            <v-tooltip :text="$t('labels.lock-nodes')" location="top">
                <template v-slot:activator="{ props }">
                    <v-btn
                        flat
                        :icon="
                            nodesDraggable
                                ? 'mdi-lock-open-variant'
                                : 'mdi-lock'
                        "
                        size="default"
                        @click="onToggleInteractive()"
                        v-bind="props"
                        variant="text"
                        color="primary"
                    ></v-btn>
                </template>
            </v-tooltip>
            <v-divider class="mx-1" vertical></v-divider>
            <v-tooltip
                :text="
                    mashupReleased
                        ? $t('strings.released-mashup')
                        : $t('labels.save')
                "
                location="top"
            >
                <template v-slot:activator="{ props }">
                    <div v-bind="props" class="d-inline-block">
                        <v-btn
                            flat
                            icon="mdi-content-save"
                            size="default"
                            :disabled="mashupReleased"
                            @click="onSave()"
                            v-bind="props"
                            variant="text"
                            color="primary"
                        >
                        </v-btn>
                    </div>
                </template>
            </v-tooltip>
            <v-tooltip :text="$t('labels.restore')" location="top">
                <template v-slot:activator="{ props }">
                    <v-btn
                        flat
                        icon="mdi-backup-restore"
                        size="default"
                        @click.stop="onRestore()"
                        v-bind="props"
                        variant="text"
                        color="primary"
                    ></v-btn>
                </template>
            </v-tooltip>
        </Controls>
    </div>
</template>

<style scoped>
.controlBanner {
    z-index: 10;
}
.controlPanel .vue-flow__controls {
    box-shadow: 0 0 11px 6px #00000020;
}
.controlPanel .vue-flow__panel.vue-flow__controls {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: white;
    padding-right: 4px;
    padding-left: 4px;
    margin-bottom: 25px;
    border-radius: 4px;
    width: auto;
}
</style>
